import React, { useState, useEffect } from "react";
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  orderBy,
  query,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db } from "./firebase";
import "./SunoMusicEditor.css";

const TAG_OPTIONS = [
  "Latin", "Contemporary", "Hip Hop", "Commercial",
  "Show Dance", "Intro", "Background", "Upbeat", "Emotional", "Epic",
];

export default function SunoMusicEditor() {
  // ── Library state ──────────────────────────────────────
  const [tracks, setTracks]               = useState([]);
  const [loading, setLoading]             = useState(true);
  const [saving, setSaving]               = useState(false);
  const [selectedTrack, setSelectedTrack] = useState(null);
  const [showForm, setShowForm]           = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [search, setSearch]               = useState("");
  const [filterTag, setFilterTag]         = useState("");

  // Form state (Save / Edit modal)
  const [formTitle, setFormTitle]   = useState("");
  const [formPrompt, setFormPrompt] = useState("");
  const [formUrl, setFormUrl]       = useState("");
  const [formTags, setFormTags]     = useState([]);
  const [formNotes, setFormNotes]   = useState("");
  const [editingId, setEditingId]   = useState(null);

  // ── Active tab ─────────────────────────────────────────
  const [activeTab, setActiveTab] = useState("generate"); // "generate" | "library"

  // ── Generate tab state ─────────────────────────────────
  const [genPrompt, setGenPrompt]     = useState("");
  const [genLyrics, setGenLyrics]     = useState("[inst]");
  const [genDuration, setGenDuration] = useState(30);
  const [genNegative, setGenNegative] = useState("");
  const [generating, setGenerating]   = useState(false);
  const [genStatus, setGenStatus]     = useState("");
  const [genAudioUrl, setGenAudioUrl] = useState("");
  const [genError, setGenError]       = useState("");

  useEffect(() => {
    loadTracks();
  }, []);

  // ── Firestore helpers ──────────────────────────────────

  async function loadTracks() {
    setLoading(true);
    try {
      const q = query(collection(db, "musicLibrary"), orderBy("createdAt", "desc"));
      const snap = await getDocs(q);
      setTracks(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error("Failed to load tracks:", e);
    }
    setLoading(false);
  }

  function openAddForm(prefill = {}) {
    setEditingId(null);
    setFormTitle(prefill.title || "");
    setFormPrompt(prefill.prompt || "");
    setFormUrl(prefill.url || "");
    setFormTags(prefill.tags || []);
    setFormNotes(prefill.notes || "");
    setShowForm(true);
  }

  function openEditForm(track) {
    setEditingId(track.id);
    setFormTitle(track.title || "");
    setFormPrompt(track.prompt || "");
    setFormUrl(track.url || "");
    setFormTags(track.tags || []);
    setFormNotes(track.notes || "");
    setShowForm(true);
  }

  async function saveTrack() {
    if (!formTitle.trim()) return;
    setSaving(true);
    try {
      const data = {
        title: formTitle.trim(),
        prompt: formPrompt.trim(),
        url: formUrl.trim(),
        tags: formTags,
        notes: formNotes.trim(),
      };
      if (editingId) {
        await updateDoc(doc(db, "musicLibrary", editingId), {
          ...data,
          updatedAt: serverTimestamp(),
        });
      } else {
        await addDoc(collection(db, "musicLibrary"), {
          ...data,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }
      setShowForm(false);
      await loadTracks();
    } catch (e) {
      console.error("Save failed:", e);
    }
    setSaving(false);
  }

  async function deleteTrack(id) {
    try {
      await deleteDoc(doc(db, "musicLibrary", id));
      if (selectedTrack?.id === id) setSelectedTrack(null);
      setDeleteConfirm(null);
      await loadTracks();
    } catch (e) {
      console.error("Delete failed:", e);
    }
  }

  function toggleTag(tag) {
    setFormTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  }

  function copyText(text) {
    navigator.clipboard.writeText(text).catch(() => {});
  }

  // ── Generate helpers ───────────────────────────────────

  async function handleGenerate() {
    if (!genPrompt.trim()) return;
    setGenerating(true);
    setGenError("");
    setGenAudioUrl("");
    setGenStatus("Submitting to ACE Step…");

    try {
      const functions = getFunctions();
      const generateMusic = httpsCallable(functions, "generateMusic");
      setGenStatus(`Generating ${genDuration}s of music — this may take 1-3 minutes…`);

      const result = await generateMusic({
        prompt:         genPrompt.trim(),
        lyrics:         genLyrics.trim() || "[inst]",
        duration:       genDuration,
        negativePrompt: genNegative.trim(),
      });

      const { audioUrl } = result.data;
      setGenAudioUrl(audioUrl);
      setGenStatus("✅ Done! Play your track below, then save it to the library.");
    } catch (err) {
      console.error("Generation failed:", err);
      setGenError(err.message || "Generation failed. Check your GoAPI key / credits.");
      setGenStatus("");
    } finally {
      setGenerating(false);
    }
  }

  function handleSaveGenerated() {
    openAddForm({
      prompt: genPrompt,
      url: genAudioUrl,
      notes: `Generated by ACE Step · ${genDuration}s · ${new Date().toLocaleDateString()}`,
    });
  }

  const filteredTracks = tracks.filter((t) => {
    const matchSearch =
      !search ||
      t.title?.toLowerCase().includes(search.toLowerCase()) ||
      t.prompt?.toLowerCase().includes(search.toLowerCase());
    const matchTag = !filterTag || (t.tags || []).includes(filterTag);
    return matchSearch && matchTag;
  });

  const allTags = [...new Set(tracks.flatMap((t) => t.tags || []))];

  // ── Render ─────────────────────────────────────────────

  return (
    <div className="sme-page">
      {/* ── Header ── */}
      <div className="sme-header">
        <div className="sme-header-left">
          <h1 className="sme-title">🎵 Music Studio</h1>
          <p className="sme-subtitle">AI-powered music generation · Greek Dance Academy</p>
        </div>
        <button className="sme-btn sme-btn--primary" onClick={() => openAddForm()}>
          + Save Track
        </button>
      </div>

      {/* ── Tabs ── */}
      <div className="sme-tabs">
        <button
          className={`sme-tab${activeTab === "generate" ? " sme-tab--active" : ""}`}
          onClick={() => setActiveTab("generate")}
        >
          ✨ Generate
        </button>
        <button
          className={`sme-tab${activeTab === "library" ? " sme-tab--active" : ""}`}
          onClick={() => setActiveTab("library")}
        >
          📚 Library {tracks.length > 0 && <span className="sme-tab-count">{tracks.length}</span>}
        </button>
      </div>

      {/* ═══════════════════════════════════════════════════
          GENERATE TAB
      ═══════════════════════════════════════════════════ */}
      {activeTab === "generate" && (
        <div className="sme-gen-layout">
          {/* ── Left: Form ── */}
          <div className="sme-gen-form-panel">
            <h2 className="sme-gen-heading">Generate Music with ACE Step</h2>
            <p className="sme-gen-note">
              Powered by ByteDance ACE Step via GoAPI ·{" "}
              <strong>$0.0005 / second</strong> of audio
            </p>

            <label className="sme-form-label">
              Style Prompt <span className="sme-required">*</span>
            </label>
            <textarea
              className="sme-textarea sme-textarea--gen"
              placeholder='e.g. "upbeat Latin samba with brass and percussion, danceable, high energy"'
              rows={3}
              value={genPrompt}
              onChange={(e) => setGenPrompt(e.target.value)}
              disabled={generating}
            />

            <label className="sme-form-label">
              Lyrics{" "}
              <span className="sme-hint">(leave [inst] for instrumental)</span>
            </label>
            <textarea
              className="sme-textarea"
              placeholder="[inst]"
              rows={4}
              value={genLyrics}
              onChange={(e) => setGenLyrics(e.target.value)}
              disabled={generating}
            />

            <label className="sme-form-label">
              Negative Prompt{" "}
              <span className="sme-hint">(optional — what to avoid)</span>
            </label>
            <input
              className="sme-input"
              placeholder='e.g. "noise, distortion, slow"'
              value={genNegative}
              onChange={(e) => setGenNegative(e.target.value)}
              disabled={generating}
            />

            <label className="sme-form-label">
              Duration: <strong>{genDuration}s</strong>{" "}
              <span className="sme-hint">
                (≈ ${(genDuration * 0.0005).toFixed(3)} · max 240s)
              </span>
            </label>
            <input
              type="range"
              className="sme-slider"
              min={10}
              max={240}
              step={5}
              value={genDuration}
              onChange={(e) => setGenDuration(Number(e.target.value))}
              disabled={generating}
            />
            <div className="sme-slider-labels">
              <span>10s</span>
              <span>60s</span>
              <span>120s</span>
              <span>180s</span>
              <span>240s</span>
            </div>

            <button
              className="sme-btn sme-btn--primary sme-btn--gen"
              onClick={handleGenerate}
              disabled={generating || !genPrompt.trim()}
            >
              {generating ? (
                <><span className="sme-spinner" /> Generating…</>
              ) : (
                "🎵 Generate Music"
              )}
            </button>

            {genStatus && !generating && (
              <p className="sme-gen-status sme-gen-status--ok">{genStatus}</p>
            )}
            {generating && genStatus && (
              <p className="sme-gen-status sme-gen-status--wait">{genStatus}</p>
            )}
            {genError && (
              <div className="sme-gen-error">
                <strong>Error:</strong> {genError}
              </div>
            )}
          </div>

          {/* ── Right: Player ── */}
          <div className="sme-gen-player-panel">
            {genAudioUrl ? (
              <div className="sme-player-wrap">
                <div className="sme-player-icon">🎧</div>
                <h3 className="sme-player-title">Your Track is Ready</h3>
                <audio controls src={genAudioUrl} className="sme-audio-player" />
                <div className="sme-player-actions">
                  <button
                    className="sme-btn sme-btn--primary"
                    onClick={handleSaveGenerated}
                  >
                    💾 Save to Library
                  </button>
                  <a
                    href={genAudioUrl}
                    download="generated-track.mp3"
                    className="sme-btn sme-btn--outline"
                  >
                    ⬇ Download MP3
                  </a>
                  <button
                    className="sme-btn sme-btn--ghost"
                    onClick={() => copyText(genAudioUrl)}
                  >
                    📋 Copy URL
                  </button>
                </div>
                <p className="sme-player-note">
                  Prompt:{" "}
                  <em>
                    {genPrompt.slice(0, 80)}
                    {genPrompt.length > 80 ? "…" : ""}
                  </em>
                </p>
              </div>
            ) : (
              <div className="sme-player-empty">
                <div className="sme-player-empty-icon">🎼</div>
                <p>Your generated track will appear here.</p>
                <p className="sme-player-empty-tip">
                  Fill in the form on the left and click{" "}
                  <strong>Generate Music</strong>.
                </p>
                <div className="sme-pricing-card">
                  <h4>💰 Pricing Guide</h4>
                  <div className="sme-pricing-row">
                    <span>30 seconds</span>
                    <span>$0.015</span>
                  </div>
                  <div className="sme-pricing-row">
                    <span>60 seconds</span>
                    <span>$0.03</span>
                  </div>
                  <div className="sme-pricing-row">
                    <span>120 seconds</span>
                    <span>$0.06</span>
                  </div>
                  <div className="sme-pricing-row">
                    <span>240 seconds</span>
                    <span>$0.12</span>
                  </div>
                  <p className="sme-pricing-note">
                    Get your API key at{" "}
                    <a
                      href="https://dashboard.goapi.ai"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      dashboard.goapi.ai ↗
                    </a>
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════
          LIBRARY TAB
      ═══════════════════════════════════════════════════ */}
      {activeTab === "library" && (
        <div className="sme-layout">
          {/* ── LEFT: Track list ── */}
          <div className="sme-sidebar">
            <div className="sme-sidebar-controls">
              <input
                className="sme-search"
                placeholder="Search tracks…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <div className="sme-tag-filters">
                <button
                  className={`sme-tag-chip${!filterTag ? " active" : ""}`}
                  onClick={() => setFilterTag("")}
                >
                  All
                </button>
                {allTags.map((tag) => (
                  <button
                    key={tag}
                    className={`sme-tag-chip${filterTag === tag ? " active" : ""}`}
                    onClick={() => setFilterTag(filterTag === tag ? "" : tag)}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>

            {loading ? (
              <div className="sme-loading">Loading library…</div>
            ) : filteredTracks.length === 0 ? (
              <div className="sme-empty">
                {tracks.length === 0
                  ? "No tracks saved yet. Generate music and save it here."
                  : "No tracks match your search."}
              </div>
            ) : (
              <div className="sme-track-list">
                {filteredTracks.map((track) => (
                  <div
                    key={track.id}
                    className={`sme-track-card${
                      selectedTrack?.id === track.id ? " selected" : ""
                    }`}
                    onClick={() => setSelectedTrack(track)}
                  >
                    <div className="sme-track-card-top">
                      <span className="sme-track-title">{track.title}</span>
                      <div
                        className="sme-track-actions"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          className="sme-icon-btn"
                          title="Edit"
                          onClick={() => openEditForm(track)}
                        >
                          ✏️
                        </button>
                        <button
                          className="sme-icon-btn sme-icon-btn--danger"
                          title="Delete"
                          onClick={() => setDeleteConfirm(track.id)}
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                    {(track.tags || []).length > 0 && (
                      <div className="sme-track-tags">
                        {track.tags.map((tag) => (
                          <span key={tag} className="sme-tag">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                    {track.prompt && (
                      <p className="sme-track-prompt-preview">
                        {track.prompt.slice(0, 80)}
                        {track.prompt.length > 80 ? "…" : ""}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── RIGHT: Track detail ── */}
          <div className="sme-main">
            {selectedTrack ? (
              <div className="sme-detail-panel">
                <div className="sme-detail-header">
                  <h2 className="sme-detail-title">{selectedTrack.title}</h2>
                  <button
                    className="sme-icon-btn"
                    onClick={() => setSelectedTrack(null)}
                    title="Close"
                  >
                    ✕
                  </button>
                </div>

                {selectedTrack.tags?.length > 0 && (
                  <div className="sme-track-tags">
                    {selectedTrack.tags.map((tag) => (
                      <span key={tag} className="sme-tag">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}

                {selectedTrack.prompt && (
                  <div className="sme-detail-section">
                    <div className="sme-detail-section-header">
                      <span className="sme-detail-section-label">Prompt</span>
                      <button
                        className="sme-copy-btn"
                        onClick={() => copyText(selectedTrack.prompt)}
                      >
                        📋 Copy
                      </button>
                    </div>
                    <div className="sme-prompt-box">{selectedTrack.prompt}</div>
                  </div>
                )}

                {selectedTrack.url && (
                  <div className="sme-detail-section">
                    <span className="sme-detail-section-label">Audio / URL</span>
                    <div className="sme-url-row">
                      <input
                        className="sme-url-input"
                        readOnly
                        value={selectedTrack.url}
                        onClick={(e) => e.target.select()}
                      />
                      <a
                        href={selectedTrack.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="sme-btn sme-btn--outline"
                      >
                        Open ↗
                      </a>
                    </div>

                    {/* Inline audio player for MP3 / CDN links */}
                    {(selectedTrack.url.match(/\.mp3(\?|$)/i) ||
                      selectedTrack.url.includes("goapi") ||
                      selectedTrack.url.includes("cdn")) && (
                      <div className="sme-embed-wrap">
                        <audio
                          controls
                          src={selectedTrack.url}
                          className="sme-audio-player"
                        />
                      </div>
                    )}

                    {/* Suno embed for suno.com/song/ links */}
                    {selectedTrack.url.includes("suno.com/song/") && (
                      <div className="sme-embed-wrap">
                        <iframe
                          src={`https://suno.com/embed/${
                            selectedTrack.url
                              .split("suno.com/song/")[1]
                              ?.split("?")[0]
                          }`}
                          width="100%"
                          height="152"
                          allow="autoplay"
                          title="Suno track"
                          className="sme-embed"
                          frameBorder="0"
                        />
                      </div>
                    )}
                  </div>
                )}

                {selectedTrack.notes && (
                  <div className="sme-detail-section">
                    <span className="sme-detail-section-label">Notes</span>
                    <p className="sme-notes">{selectedTrack.notes}</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="sme-detail-empty">
                <div className="sme-detail-empty-icon">🎵</div>
                <p>Select a track from the list to view details.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Save / Edit Form Modal ── */}
      {showForm && (
        <div className="sme-modal-overlay" onClick={() => setShowForm(false)}>
          <div className="sme-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sme-modal-header">
              <h2>{editingId ? "Edit Track" : "Save Track"}</h2>
              <button className="sme-icon-btn" onClick={() => setShowForm(false)}>
                ✕
              </button>
            </div>

            <div className="sme-form">
              <label className="sme-form-label">Title *</label>
              <input
                className="sme-input"
                placeholder="e.g. Latin Samba Intro"
                value={formTitle}
                onChange={(e) => setFormTitle(e.target.value)}
              />

              <label className="sme-form-label">Prompt</label>
              <textarea
                className="sme-textarea"
                placeholder="Style prompt used to generate this track…"
                rows={3}
                value={formPrompt}
                onChange={(e) => setFormPrompt(e.target.value)}
              />

              <label className="sme-form-label">Track URL / MP3</label>
              <input
                className="sme-input"
                placeholder="https://… (Suno link or MP3 URL)"
                value={formUrl}
                onChange={(e) => setFormUrl(e.target.value)}
              />

              <label className="sme-form-label">Tags</label>
              <div className="sme-tag-picker">
                {TAG_OPTIONS.map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    className={`sme-tag-chip${
                      formTags.includes(tag) ? " active" : ""
                    }`}
                    onClick={() => toggleTag(tag)}
                  >
                    {tag}
                  </button>
                ))}
              </div>

              <label className="sme-form-label">Notes</label>
              <textarea
                className="sme-textarea"
                placeholder="Any extra notes about this track…"
                rows={2}
                value={formNotes}
                onChange={(e) => setFormNotes(e.target.value)}
              />

              <div className="sme-modal-footer">
                <button
                  className="sme-btn sme-btn--ghost"
                  onClick={() => setShowForm(false)}
                >
                  Cancel
                </button>
                <button
                  className="sme-btn sme-btn--primary"
                  onClick={saveTrack}
                  disabled={saving || !formTitle.trim()}
                >
                  {saving
                    ? "Saving…"
                    : editingId
                    ? "Save Changes"
                    : "Save Track"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Confirm Modal ── */}
      {deleteConfirm && (
        <div
          className="sme-modal-overlay"
          onClick={() => setDeleteConfirm(null)}
        >
          <div
            className="sme-modal sme-modal--sm"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: "0 0 0.8rem" }}>Delete this track?</h3>
            <p
              style={{
                color: "#888",
                fontSize: "0.9rem",
                margin: "0 0 1.5rem",
              }}
            >
              This action cannot be undone.
            </p>
            <div className="sme-modal-footer">
              <button
                className="sme-btn sme-btn--ghost"
                onClick={() => setDeleteConfirm(null)}
              >
                Cancel
              </button>
              <button
                className="sme-btn sme-btn--danger"
                onClick={() => deleteTrack(deleteConfirm)}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
